<template>
    <div>
      <slide-over-panel v-model:show="showNotifications">
        <template #title>Alarm Notification Log</template>
        <template #content>
          <slot name="notifications">
          </slot>
        </template>
      </slide-over-panel>
      <form :action="formLink" method="POST">
          <input type="hidden" name="_token" :value="csrf" />
        <div    v-if="canUpdateAlarms"
                class="flex justify-center">
          <div class="flex flex-wrap md:w-full pt-2">
            <!--            Tailwind UI -> Navigation -> Tabs -> Full-width tabs with Underline-->
            <div class="w-full">
              <div class="sm:hidden pb-4 sm:pb-0">
                <select @change="showViaSelect" aria-label="Selected tab" class="w-full bg-white block focus:ring-blue-500 focus:border-blue-500 w-full shadow-sm sm:max-w-xs sm:text-sm border-gray-300 rounded-md">
                  <option disabled selected>Select an action</option>
                  <option value="comment" :selected="commentOnly || comment">Comment</option>
                  <option v-if="showSnoozeAction" value="snooze" :selected="snooze">Snooze</option>
                  <option v-if="showUnsnooze" value="cancel-snooze" :selected="cancelSnooze">Unsnooze</option>
                  <option value="resolve" :selected="resolve">Resolve</option>
                </select>
              </div>
              <div class="hidden sm:block">
                <div class="border-b border-gray-200">
                  <nav class="-mb-px flex">
                    <a
                      @click="showComment"
                      v-bind:class="{ 'text-blue-600 border-blue-500 focus:outline-none focus:text-blue-800 focus:border-blue-700': comment,
                               'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-300': !comment }"
                      :class="tabWidth"
                      class="no-underline hover:no-underline focus:no-underline py-4 px-1 text-center border-b-2 font-medium text-lg leading-5 cursor-pointer">
                      Comment
                    </a>
                    <!-- Snooze is always offered. On an already-snoozed alarm it extends the
                         snooze by overwriting snooze_until, which does NOT renotify; making the
                         user unsnooze first would (ENGS-1415). Unsnooze is an additional tab. -->
                    <a
                      v-if="showSnoozeAction"
                      @click="showSnooze"
                      v-bind:class="{ 'text-blue-600 border-blue-500 focus:outline-none focus:text-blue-800 focus:border-blue-700': snooze,
                               'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-300': !snooze }"
                      :class="tabWidth"
                      class="no-underline hover:no-underline focus:no-underline py-4 px-1 text-center border-b-2 font-medium text-lg leading-5 cursor-pointer">
                      Snooze
                    </a>
                    <a
                      v-if="showUnsnooze"
                      @click="showCancelSnooze"
                      v-bind:class="{ 'text-blue-600 border-blue-500 focus:outline-none focus:text-blue-800 focus:border-blue-700': cancelSnooze,
                               'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-300': !cancelSnooze }"
                      :class="tabWidth"
                      class="no-underline hover:no-underline focus:no-underline py-4 px-1 text-center border-b-2 font-medium text-lg leading-5 cursor-pointer">
                      Unsnooze
                    </a>
                    <a
                      @click="showResolve"
                      v-bind:class="{ 'text-blue-600 border-blue-500 focus:outline-none focus:text-blue-800 focus:border-blue-700': resolve,
                               'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-300': !resolve }"
                      :class="tabWidth"
                      class="no-underline hover:no-underline focus:no-underline py-4 px-1 text-center border-b-2 font-medium text-lg leading-5 cursor-pointer">
                      Resolve
                    </a>
                  </nav>
                </div>
              </div>
            </div>
            <alarm-address-comment v-if="comment"
                                   :send-event-only="sendEventOnly"
                                   :require-comment="requireComment"
                                   @adding-comment="$emit('adding-comment', $event)"

            ></alarm-address-comment>
            <alarm-address-snooze   v-if="snooze && !commentOnly" :send-event-only="sendEventOnly"
                                    :timezone="timezone"
                                    :require-comment="requireComment"
                                    :max-snooze-days="maxSnoozeDays"
                                    :is-snoozed="isSnoozed"
                                    :mass-mode="massMode"
                                    @snoozing-alarm="$emit('snoozing-alarm', $event)"
            ></alarm-address-snooze>
            <alarm-address-resolve  v-if="resolve && !commentOnly"
                                    :is-mass-resolving = isMassResolving
                                    :send-event-only="sendEventOnly"
                                    :require-comment="requireComment"
                                    @resolving-alarm="$emit('resolving-alarm', $event)"
            ></alarm-address-resolve>
            <alarm-address-cancel-snooze v-if="cancelSnooze && !commentOnly"
                                         :send-event-only="sendEventOnly"
                                         :require-comment="requireComment"
                                         @cancelling-snooze="$emit('cancelling-snooze', $event)"
            ></alarm-address-cancel-snooze>
            <div v-if="!snooze && !comment && !resolve && !cancelSnooze"
                 class="w-full bg-white border border-t-0 border-gray-200 overflow-hidden sm:rounded-b-lg hidden sm:flex min-h-[16rem] items-center justify-center p-8">
              <div class="flex flex-col items-center justify-center space-y-0.5 text-center">
                <span class="text-gray-500 mb-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 12l3 0" /><path d="M12 3l0 3" /><path d="M7.8 7.8l-2.2 -2.2" /><path d="M16.2 7.8l2.2 -2.2" /><path d="M7.8 16.2l-2.2 2.2" /><path d="M12 12l9 3l-4 2l-2 4l-3 -9" /></svg>
                </span>
                <span class="text-base font-medium text-gray-900">Select an action</span>
                <span class="text-sm text-gray-500">Choose {{ showSnoozeAction ? 'Comment, Snooze, or Resolve' : 'Comment or Resolve' }} above to get started.</span>
              </div>
            </div>
            <div v-if="commentOnly && (resolve || snooze)"
                 class="w-full bg-white border border-t-0 border-gray-200 overflow-hidden sm:rounded-b-lg flex min-h-[16rem] items-center justify-center p-8">
              <div class="flex flex-col items-center justify-center space-y-0.5 text-center">
                <span class="text-green-500 mb-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M9 12l2 2l4 -4" /></svg>
                </span>
                <span class="text-base font-medium text-gray-900">Alarm resolved</span>
                <span class="text-sm text-gray-500">This alarm is closed. You can still leave a comment for the record.</span>
              </div>
            </div>
          </div>
        </div>
      </form>

      <alarm-comments :comments='comments'
                      @show-notifications="showNotifications = !showNotifications"></alarm-comments>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {UrlParamsMixin} from "@/mixins/UrlParamsMixin";
import AlarmAddressSnooze from "./alarmAddressSnooze";
import AlarmAddressComment from "./alarmAddressComment";
import AlarmAddressResolve from "./alarmAddressResolve";
import AlarmAddressCancelSnooze from "./alarmAddressCancelSnooze";
import AlarmComments from "@/components/alarmComments";
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue";
import SonicuButton from "@/components/elements/buttons/SonicuButton.vue";

    export default {
        name: "alarmAddress",
        components: {
          SonicuButton,
          AlarmAddressSnooze, AlarmAddressComment, AlarmAddressResolve, AlarmAddressCancelSnooze, AlarmComments, SlideOverPanel},
        mixins: [SonicuCoreMixin, UrlParamsMixin],
        props: {
            serverComments: {
                type: Array,
                default: () => [],
            },
            commentOnly: {
                type: Boolean,
                default: false,
            },
            timezone: String,
            sendEventOnly: {
                type: Boolean,
                default: false,
            },
            isMassResolving: {
                type: Boolean,
                default: false,
            },
            requireComment: {
                type: Boolean,
                default: false,
            },
            maxSnoozeDays: {
                type: Number,
                default: null,
            },
            isSnoozed: {
                type: Boolean,
                default: false,
            },
            // A maintenance alarm's snooze is its maintenance window, which the server refuses to
            // change manually (both snooze and cancel-snooze return 422), so hide those actions.
            isMaintenance: {
                type: Boolean,
                default: false,
            },
            // Mass-address mode: a batch has no single snooze state, so show Snooze and
            // Unsnooze as separate actions rather than the single-alarm toggle.
            massMode: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                snooze: false,
                comment: this.commentOnly,
                comments: this.serverComments,
                resolve: false,
                cancelSnooze: false,
                showNotifications: false,
            }
        },
        mounted() {
            const action = this.urlParamsGet('action');
            if (action === 'resolve') {
                this.showResolve();
            } else if (action === 'snooze' && this.showSnoozeAction) {
                this.showSnooze();
            } else if (action === 'comment') {
                this.showComment();
            } else if (action === 'cancel-snooze' && this.showUnsnooze) {
                this.showCancelSnooze();
            }
        },
        methods: {
            showComment(event) {
                this.resolve = false;
                this.snooze = false;
                this.comment = true;
                this.cancelSnooze = false;
            },
            showSnooze(event) {
                this.comment = false;
                this.resolve = false;
                this.snooze = true;
                this.cancelSnooze = false;
            },
            showResolve(event) {
                this.comment = false;
                this.snooze = false;
                this.resolve = true;
                this.cancelSnooze = false;
            },
            showCancelSnooze(event) {
                this.comment = false;
                this.snooze = false;
                this.resolve = false;
                this.cancelSnooze = true;
            },
            showViaSelect(event) {
                if (event.target.value === 'comment') {
                    this.showComment()
                } else if (event.target.value === 'snooze') {
                    this.showSnooze()
                } else if (event.target.value === 'cancel-snooze') {
                    this.showCancelSnooze()
                } else if (event.target.value === 'resolve') {
                    this.showResolve()
                } else {
                    this.comment = false;
                    this.snooze = false;
                    this.resolve = false;
                    this.cancelSnooze = false;
                }
            },
            canUpdateAlarms() {
                return this.permissions.can('update_alarms')
            },
        },
        computed: {
            formLink() {
                return window.location.pathname
            },
            // Mass mode skips maintenance alarms server-side, so it always offers Snooze.
            showSnoozeAction() {
                return this.massMode || ! this.isMaintenance
            },
            // Mass mode always offers Unsnooze because a batch has no single snooze state.
            // A single alarm only offers it when that alarm is actually snoozed.
            showUnsnooze() {
                return this.massMode || (this.isSnoozed && ! this.isMaintenance)
            },
            tabWidth() {
                const tabCount = 2 + (this.showSnoozeAction ? 1 : 0) + (this.showUnsnooze ? 1 : 0)

                return {2: 'w-1/2', 3: 'w-1/3', 4: 'w-1/4'}[tabCount]
            },
        }
    }
</script>

<style scoped>

</style>